/**
 * Proactive session-refresh scheduler.
 *
 * Schedules a silent token refresh 2 minutes before expiry. If the refresh
 * succeeds it re-schedules for the new token; if it fails it emits the
 * ignis:session-expired event so the UI can prompt the user.
 *
 * Usage:
 *   startSessionRefresh()  — call when the user becomes authenticated
 *   stopSessionRefresh()   — call on logout
 */

import { getAuthApiService } from './authApi'
import { emitSessionExpired } from './sessionEvents'

const BUFFER_MS = 120_000 // refresh 2 minutes before expiry
const MIN_DELAY_MS = 5_000 // always wait at least 5 seconds

let timerId: ReturnType<typeof setTimeout> | null = null

function schedule(): void {
  const svc = getAuthApiService()
  const expiry = svc.getTokenExpiryMs()

  if (!expiry) {
    // No stored token — nothing to schedule
    return
  }

  const delay = Math.max(expiry - Date.now() - BUFFER_MS, MIN_DELAY_MS)

  timerId = setTimeout(async () => {
    timerId = null
    const token = await svc.ensureValidToken(BUFFER_MS)
    if (token) {
      schedule() // reschedule for the new token
    } else {
      emitSessionExpired()
    }
  }, delay)
}

export function startSessionRefresh(): void {
  // Idempotent — clear any existing timer before scheduling
  stopSessionRefresh()
  schedule()
}

export function stopSessionRefresh(): void {
  if (timerId !== null) {
    clearTimeout(timerId)
    timerId = null
  }
}
